import {
  type InfiniteData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { computed, type MaybeRef, unref } from "vue";

import { components as OpenAPI } from "@/autogen/openapi";
import type { Parameter } from "@/components/Layout/ParameterWidget/lib";
import { ok } from "@/lib/openapi";
import type { SearchResultWithId } from "@/types/search_result";

import api from "../../base";
import { useGenericAggregateQuery } from "../generic";
import {
  findItemInInfiniteQueries,
  findItemInListQueries,
  useNewsletterId,
} from "../utils";
import type { AutomationPage } from "./list";

export type Automation = OpenAPI["schemas"]["Automation"];

export type AutomationCreateInput = OpenAPI["schemas"]["AutomationInput"];

export type AutomationUpdateInput =
  OpenAPI["schemas"]["AutomationUpdateInput"] & { id: string };

export const useAutomationDetailQuery = ({
  id,
}: {
  id: MaybeRef<string | undefined>;
}) => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const query = useQuery({
    queryKey: [newsletterId, "automations", "detail", id],
    enabled: () => unref(id) !== undefined,
    async queryFn({ signal }): Promise<Automation> {
      const data = await ok(
        api.get("/automations/{id}", {
          signal,
          params: {
            path: {
              id: unref(id)!,
            },
          },
        })
      );

      return data;
    },
  });

  const data = query.data;
  const dataWithFallback = computed(() => {
    if (data.value) {
      return data.value;
    }

    const needle = unref(id);

    {
      const queries = queryClient.getQueriesData<InfiniteData<AutomationPage>>({
        queryKey: [newsletterId, "automations", "list"],
      });

      const found = findItemInInfiniteQueries<Automation>(
        queries,
        (item) => item.id === needle
      );

      if (found) {
        return found;
      }
    }

    {
      const queries = queryClient.getQueriesData<SearchResultWithId[]>({
        queryKey: [newsletterId, "search"],
      });

      type AutomationSearchResult = SearchResultWithId & { type: "automation" };

      // this would've looked nicer if TypeScript didn't lose the type narrowing
      // after adding the `.obj.id` condition :(
      const found = findItemInListQueries<
        SearchResultWithId,
        AutomationSearchResult
      >(queries, (item): item is AutomationSearchResult => {
        return item.type === "automation" && item.obj.id === needle;
      });

      if (found) {
        return found.obj satisfies Automation;
      }
    }

    return undefined;
  });

  return { ...query, dataWithFallback };
};

export const useAutomationAttemptAggregateQuery = ({
  id,
}: {
  id: MaybeRef<string>;
}) => {
  const newsletterId = useNewsletterId();

  const query = useGenericAggregateQuery<Parameter<"status" | "automation">>({
    queryKey: [newsletterId, "automations", "attempt-aggregate", id],
    resource: "automation_attempt",
    parameters: computed(() => {
      return {
        automation: [unref(id)],
        status: [],
      };
    }),
  });

  return query;
};

export const useAutomationAnalyticsQuery = ({
  id,
}: {
  id: MaybeRef<string>;
}) => {
  const newsletterId = useNewsletterId();

  const query = useQuery({
    queryKey: [newsletterId, "automations", "analytics", id],
    async queryFn({ signal }) {
      const data = await ok(
        api.get("/automations/{id}/analytics", {
          signal,
          params: {
            path: { id: unref(id) },
          },
        })
      );

      return data;
    },
  });

  return query;
};

export const useCreateAutomationMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn(input: AutomationCreateInput): Promise<Automation> {
      const data = await ok(
        api.post("/automations", {
          body: input,
        })
      );

      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        [newsletterId, "automations", "detail", data.id],
        data
      );

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "automations", "list"],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "search"],
      });
    },
  });

  return mutation;
};

export const useUpdateAutomationMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn({
      id,
      ...input
    }: AutomationUpdateInput): Promise<Automation> {
      const data = await ok(
        api.patch("/automations/{id}", {
          params: {
            path: { id },
          },
          body: input,
        })
      );

      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        [newsletterId, "automations", "detail", data.id],
        data
      );

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "automations", "list"],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "search"],
      });
    },
  });

  return mutation;
};

export const useDeleteAutomationMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn({ id }: { id: string }): Promise<void> {
      await ok(
        api.delete("/automations/{id}", {
          params: {
            path: { id },
          },
        })
      );
    },
    onSuccess(_data, { id }) {
      queryClient.resetQueries({
        queryKey: [newsletterId, "automations", "detail", id],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "automations", "list"],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "search"],
      });
    },
  });

  return mutation;
};
